import type { Permission } from '@elsa/contracts';
import { cx, Link } from '@elsa/ui';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';
import { Logo } from '../brand.tsx';
import { daysLeft } from '../lib/format.ts';
import { useOrg } from '../lib/session.tsx';
import { app, must, rpc, supabase } from '../lib/supabase.ts';
import type { OrgCpv } from '../lib/types.ts';

// Meniul depinde de rol: o intrare apare daca utilizatorul are macar una din permisiunile
// din `need` (fara `need`: pentru toti). Aceleasi reguli pazesc si rutele (AppShell).
export const NAV: { to: string; label: string; need?: Permission[] }[] = [
  { to: '/app', label: 'Acasă' },
  { to: '/app/achizitii', label: 'Achiziții', need: ['data.read'] },
  { to: '/app/produse', label: 'Produse', need: ['data.read'] },
  { to: '/app/preturi', label: 'Prețuri', need: ['data.read'] },
  { to: '/app/alerte', label: 'Alerte', need: ['alerts.own', 'alerts.team'] },
  { to: '/app/notificari', label: 'Notificări' },
  { to: '/app/echipa', label: 'Echipă', need: ['members.manage', 'sales.view'] },
  { to: '/app/roluri', label: 'Roluri', need: ['roles.manage'] },
  { to: '/app/setari', label: 'Setări firmă', need: ['settings.manage'] },
  { to: '/app/abonament', label: 'Abonament', need: ['billing.manage'] },
  { to: '/app/cont', label: 'Cont' },
];

export function allowed(can: (p: Permission) => boolean, need?: Permission[]): boolean {
  return !need || need.some(can);
}

/** Codurile CPV ale firmei curente (Setari, banda de sus). */
export function useOrgCpv() {
  const { org } = useOrg();
  return useQuery({
    queryKey: ['org_cpv', org.id],
    queryFn: () => rpc<OrgCpv>('org_cpv', { p_org: org.id }),
  });
}

function active(path: string, to: string): boolean {
  return to === '/app' ? path === '/app' : path === to || path.startsWith(`${to}/`);
}

export function useUnread() {
  const { session } = useOrg();
  return useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: async () => {
      const { count, error } = await app()
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', session.user.id)
        .is('read_at', null);
      if (error) throw new Error(error.message);
      return count ?? 0;
    },
  });
}

/** Benzile de sus: verde = informatie (proba), rosu = alerta (nu se vad date). */
const banner = {
  info: 'border-b-2 border-cod bg-verde px-4 py-2.5 text-sm text-hartie sm:px-6',
  alert: 'border-b-2 border-cod bg-semnal px-4 py-2.5 text-sm font-medium text-cod sm:px-6',
};
const bannerLink = 'font-bold underline underline-offset-3';

/** Banda de sus: proba (cate zile au ramas) sau lipsa planului. */
function PlanBanner() {
  const { entitlements: e, can } = useOrg();
  if (!e) return null;
  if (!e.plan) {
    return (
      <div className={banner.alert}>
        Firma nu are un abonament activ, deci datele SEAP nu se văd.{' '}
        {can('billing.manage') ? (
          <Link to="/app/abonament" className={bannerLink}>
            Alege un plan
          </Link>
        ) : (
          'Cere proprietarului firmei să aleagă un plan.'
        )}
      </div>
    );
  }
  if (e.plan === 'trial' && e.subscription?.trial_end) {
    const d = daysLeft(e.subscription.trial_end);
    return (
      <div className={banner.info}>
        Perioadă de probă: {d === 1 ? 'o zi rămasă' : `${d} zile rămase`}.{' '}
        {can('billing.manage') ? (
          <Link to="/app/abonament" className={bannerLink}>
            Vezi planurile
          </Link>
        ) : null}
      </div>
    );
  }
  return null;
}

/** A doua banda: firma cu plan, dar fara coduri CPV, nu vede nicio achizitie. */
function CpvBanner() {
  const { entitlements: e, can } = useOrg();
  const cpv = useOrgCpv();
  if (!e?.plan || !cpv.data || cpv.data.used > 0) return null;
  return (
    <div className={banner.alert}>
      Firma nu urmărește încă niciun cod CPV, deci nu se văd achiziții.{' '}
      {can('settings.manage') ? (
        <Link to="/app/setari" className={bannerLink}>
          Alege codurile
        </Link>
      ) : (
        'Cere administratorului firmei să le aleagă din Setări.'
      )}
    </div>
  );
}

// Bara laterala, ca in macheta din kit: Verde Adanc, pagina curenta pe Negru Cod, firma jos.
export function AppLayout({ path, children }: { path: string; children: ReactNode }) {
  const { org, orgs, setOrg, session, can } = useOrg();
  const unread = useUnread();
  const [open, setOpen] = useState(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: efectul tine doar de schimbarea paginii
  useEffect(() => setOpen(false), [path]);
  const links = (
    <nav aria-label="Aplicația" className="flex flex-col gap-[2px]">
      {NAV.filter((n) => allowed(can, n.need)).map((n) => (
        <Link
          key={n.to}
          to={n.to}
          className={cx(
            'flex items-center justify-between px-3 py-2.5 text-sm',
            active(path, n.to)
              ? 'bg-cod font-bold text-hartie'
              : 'text-otel hover:bg-cod/40 hover:text-hartie',
          )}
        >
          {n.label}
          {n.to === '/app/notificari' && unread.data ? (
            <span className="bg-semnal px-1.5 py-0.5 text-[11px] leading-none font-extrabold text-cod tabular-nums">
              {unread.data}
            </span>
          ) : null}
        </Link>
      ))}
    </nav>
  );
  const footer = (
    <div className="space-y-3 border-t-2 border-grafit px-3 pt-3 text-xs text-otel">
      <div className="space-y-1">
        {orgs.length > 1 ? (
          <select
            aria-label="Firma"
            value={org.id}
            onChange={(e) => setOrg(e.target.value)}
            className="w-full border border-grafit bg-cod px-2 py-1.5 text-sm font-semibold text-hartie outline-none focus:border-semnal"
          >
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        ) : (
          <div className="truncate text-sm font-semibold text-hartie" title={org.name}>
            {org.name}
          </div>
        )}
        <Link to="/app/firma-noua" className="block hover:text-hartie hover:underline">
          + altă firmă
        </Link>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="truncate" title={session.user.email}>
          {session.user.email}
        </span>
        <button
          type="button"
          className="shrink-0 font-bold text-hartie hover:text-semnal"
          onClick={() => void supabase.auth.signOut()}
        >
          Ieși
        </button>
      </div>
    </div>
  );
  return (
    <div className="min-h-dvh lg:flex">
      {/* Coloana verde tine toata inaltimea paginii; continutul ei ramane pe ecran la scroll. */}
      <div className="hidden w-60 shrink-0 bg-verde lg:block">
        <aside className="sticky top-0 flex h-dvh flex-col justify-between gap-6 overflow-y-auto px-4 py-5">
          <div className="space-y-6">
            <Link to="/app" className="block px-3 pt-1">
              <Logo variant="compact" height={26} />
            </Link>
            {links}
          </div>
          {footer}
        </aside>
      </div>
      <div className="sticky top-0 z-10 lg:hidden">
        <header className="flex items-center justify-between border-b-2 border-grafit bg-cod px-4 py-2.5">
          <Link to="/app">
            <Logo variant="compact" height={22} />
          </Link>
          <button
            type="button"
            aria-expanded={open}
            className="border-2 border-grafit px-3 py-1.5 text-sm font-bold text-hartie hover:border-hartie"
            onClick={() => setOpen(!open)}
          >
            {open ? 'Închide' : `Meniu${unread.data ? ` (${unread.data})` : ''}`}
          </button>
        </header>
        {open ? (
          <div className="max-h-[calc(100dvh-58px)] space-y-4 overflow-y-auto border-b-2 border-cod bg-verde p-4">
            {links}
            {footer}
          </div>
        ) : null}
      </div>
      <main className="min-w-0 flex-1 bg-hartie">
        <PlanBanner />
        <CpvBanner />
        {children}
      </main>
    </div>
  );
}

export async function markRead(ids: number[]): Promise<void> {
  if (!ids.length) return;
  await must(
    app().from('notifications').update({ read_at: new Date().toISOString() }).in('id', ids),
  );
}
